import { useAxiosPrivate, useDocumentTitle } from '../../hooks'
import { PAGE_TITLE } from '../../constants/constants'
import {
  DefaultToolbarContainer,
  KcTable,
} from '../../components/common/kc-table'
import { Box, Button, LinearProgress, Typography } from '@mui/material'
import { CustomNoRowsOverlay } from '../../components/common/kc-table/components'
import GlobalFilterForm from '../../components/common/GlobalFilterForm'
import { parseAsInteger, useQueryState } from 'nuqs'
import { useRef, useMemo, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  dicomLogActions,
  fetchDicomLogs,
  getDicomLogs,
  getDicomLogsError,
  getDicomLogsLoading,
} from '../slices/dicomLog.slice'
import {
  fetchFacilities,
  getFacilities,
  getFetchError,
  getFetchLoading,
} from '../slices/facility.slice'
import dayjs from 'dayjs'
import { getColumns } from './columns'
import { formatDicomLogs } from './utils'
import Modal from '../../components/common/Modal'
import DicomLogDetail from '../../components/DicomLogDetail'
import Layout from '../../layouts/common/Layout'
import { API_URL } from '../../api/constants'
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'

function DicomSenderLogs() {
  useDocumentTitle(PAGE_TITLE.DICOM_LOGS)

  const [pageNo, setPage] = useQueryState(
    'pageNo',
    parseAsInteger.withDefault(0)
  )
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(50)
  )

  const [id, setId] = useQueryState('id', { type: 'string' })

  const modalRef = useRef(null)
  const axiosPrivate = useAxiosPrivate()
  const dispatch = useDispatch()

  const dicomeSenderLogsData = useSelector(getDicomLogs)
  const error = useSelector(getDicomLogsError)
  const loading = useSelector(getDicomLogsLoading)
  const fetchLoading = useSelector(getFetchLoading)
  const fetchError = useSelector(getFetchError)
  const facilitiesData = useSelector(getFacilities)

  const queryOptions = useMemo(() => ({ pageNo, pageSize }), [pageNo, pageSize])

  const formattedDicomLogs = useMemo(() => {
    if (fetchError) {
      console.error('Error fetching facilities:', fetchError)
      return []
    }
    if (fetchLoading) {
      return []
    }
    return formatDicomLogs(
      dicomeSenderLogsData?.listOfLogs,
      facilitiesData || []
    )
  }, [fetchError, fetchLoading, dicomeSenderLogsData, facilitiesData])

  const buildQueryString = useCallback(
    (options) => {
      const queryParams = {
        pageNo,
        pageSize,
        ...(options?.filter && options?.value
          ? { [options.filter]: options.value }
          : {}),
        ...(options?.startDate && options?.endDate
          ? {
              from_date: dayjs(options.startDate).format('YYYY-MM-DD'),
              till_date: dayjs(options.endDate).format('YYYY-MM-DD'),
            }
          : {}),
      }

      return Object.entries(queryParams)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join('&')
    },
    [pageNo, pageSize]
  )

  const fetchDicomLogsData = useCallback(
    (options = {}) => {
      const queryString = buildQueryString(options)
      dispatch(
        fetchDicomLogs({
          axiosInstance: axiosPrivate,
          path: `${API_URL.DICOM_SENDER_LOGS}?${queryString}`,
        })
      )
    },
    [axiosPrivate, buildQueryString, dispatch]
  )
  useEffect(() => {
    dispatch(fetchFacilities(axiosPrivate))
  }, [dispatch, axiosPrivate])

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    fetchDicomLogsData({
      ...queryOptions,
      filter: queryParams.get('filter'),
      value: queryParams.get('value'),
      startDate: queryParams.get('startDate'),
      endDate: queryParams.get('endDate'),
    })
  }, [queryOptions, fetchDicomLogsData])

  const columns = getColumns({
    onView: (id) => () => {
      setId(id)
      dispatch(dicomLogActions.setSelectedLogId(id))
      modalRef?.current?.open()
    },
  })

  const initialState = {
    sorting: {
      sortModel: [{ field: 'createdAt', sort: 'desc' }],
    },
    pagination: {
      page: pageNo,
      pageSize: pageSize,
    },
  }

  const renderError = () => {
    if (fetchError) {
      return (
        <Layout.CenterWrapper>
          <Typography variant="h6" color="error">
            Error fetching DICOM logs: {fetchError.message}
          </Typography>
        </Layout.CenterWrapper>
      )
    }
    console.log('Error fetching DICOM logs:', error)
    if (error?.status === 401) {
      return (
        <Layout.CenterWrapper>
          <Typography variant="h6" color="error">
            Unauthorized access. Please log in again.
          </Typography>
        </Layout.CenterWrapper>
      )
    }
    return (
      <Layout.CenterWrapper>
        <Typography variant="h6" color="error">
          An error occurred while fetching DICOM logs. Please try again later.
        </Typography>
      </Layout.CenterWrapper>
    )
  }

  if (error && error?.status !== 421) {
    return renderError()
  }

  const dicomSenderLogIds = formattedDicomLogs?.map((log) => log.id) || []
  const currentSelectedIndex = dicomSenderLogIds?.indexOf(id)
  const hasPrevious = currentSelectedIndex > 0
  const hasNext = currentSelectedIndex < dicomSenderLogIds.length - 1
  return (
    <div style={{ height: 'auto' }}>
      <Modal
        onClose={() => {
          setId(null)
          dispatch(dicomLogActions.clearDicomEventDetails())
          modalRef?.current?.close()
        }}
        ref={modalRef}
      >
        <Box display="flex" alignItems="center" gap={2} ml="auto" sx={{
          position: 'absolute',
          top: '.75rem',
          right: '4rem'
        }}>
          <Button
            variant='outlined'
            onClick={() => setId(dicomSenderLogIds[currentSelectedIndex - 1])}
            disabled={!hasPrevious}
            title="Previous log"
            startIcon={<ArrowBackIos />}
          >
            Previous
          </Button>
                    <Button
            variant='outlined'
            onClick={() => setId(dicomSenderLogIds[currentSelectedIndex + 1])}
            disabled={!hasNext}
            title="Next log"
            endIcon={<ArrowForwardIos />}
          >
            Next
          </Button>

        </Box>
                  <DicomLogDetail logId={id} />

      </Modal>
      <GlobalFilterForm
        filters={[
          {
            title: 'KardiaPro Team',
            value: 'kpro_team_id',
            valueInputType: 'autocomplete',
            valueOptions: facilitiesData || [],
            valueOptionLabel: (facility) => facility.facility_name,
            valueOptionValue: (facility) => facility.kpro_team_id,
          },
          {
            title: 'Event ID',
            value: 'event_id',
            valueInputType: 'text',
          },
          {
            title: 'Facility Name',
            value: 'facility_name',
            valueInputType: 'text',
          },
        ]}
        loading={fetchLoading}
        error={fetchError}
        fetchButtonLabel="Fetch Logs"
        resetButtonLabel="Reset"
        onFetch={fetchDicomLogsData}
        onReset={() => fetchDicomLogsData({})}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 200,
        }}
      >
        <KcTable
          rows={formattedDicomLogs || []}
          autoHeight
          maxHeight="100%"
          columns={columns}
          getRowId={(row) => row.id}
          rowCount={dicomeSenderLogsData?.totalCount || 0}
          page={pageNo}
          pageSize={pageSize}
          paginationMode="server"
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pagination
          loading={loading}
          sortingOrder={['desc', 'asc']}
          initialState={initialState}
          rowsPerPageOptions={[5, 10, 25, 50]}
          components={{
            LoadingOverlay: LinearProgress,
            Toolbar: DefaultToolbarContainer,
            NoResultsOverlay: () => (
              <CustomNoRowsOverlay text="No Logs Available" />
            ),
            NoRowsOverlay: () => <CustomNoRowsOverlay text="No Logs Found" />,
          }}
        />
      </div>
    </div>
  )
}

export default DicomSenderLogs
